import {
  select,
  event,
  format,
  timeFormat,
  min,
  curveLinear,
  line as lineD3,
} from "d3";
import {
  europeanBlackScholesFX,
  qVolatility,
  strikeToDelta,
} from "@/_core/_helpers/_quadraticVolatility";
import {
  bandsCreate,
  dotCreate,
  hLineCreate,
  rectsCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import { populateTableComparison } from "@/_core/data/Markets";
import {
  colorDarkExpo,
  darkBackground4,
  grayLightBackground,
  lightbackground2,
  textactive,
  textactive2,
  colorExpo,
} from "@/_core/_helpers/_parametric_tools";
import { bandsCreateCA } from "./D3ChartHoverHelpers";
import { createTooltips } from "./TechnicalAnalysis";
import { cleanAndUnionName } from "./D3Chart";
import { textinactive } from "../_helpers/_parametric_tools";
// ==== Custom functions for Percentiles Chart ==================================================================
export const D3HoverPercentiles = function() {};

D3HoverPercentiles.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _dateFormat,
  _currencyFormat,
  _formatPercent,
  colorTheme,
  _context,
) {
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  this._formatPercent = _formatPercent;
  var _this = this;

  dotCreate(
    svg,
    `${customName}`,
    2,
    textactive2(colorTheme),
    colorExpo(colorTheme),
    1.5,
  );

  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let tooltipWidth2 = 80;
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .style("color", textactive2(colorTheme))
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");
  let TooltipB = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("color", textactive2(colorTheme))
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .style("color", textactive2(colorTheme))
    .style("background", lightbackground2(colorTheme))
    .style("border", lightbackground2(colorTheme))
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");

  var mouseOver = function(d, i) {
    select(".bandsPERC" + i).classed("hoved", true);
    if (_context.isCandles) {
      select(".stickPERC" + i).classed("hoved", true);
      select(".stick2PERC" + i).classed("hoved", true);
      select(".candlePERC" + i).classed("hoved", true);
    } else {
      select(".dot" + customName)
        .style("opacity", 1)
        .attr("transform", `translate(${svg.x(d.date)},${svg.y(d.close)})`);
    }

    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    // const yPixels = svg.y(d.close);
    select(".hHoverLinePERC")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    Tooltip.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .html(format(_this._currencyFormat)(svg.y.invert(yPixels)));

    Tooltip2.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_this._dateFormat)(d.date));

    _this.displayOnHover(d, _this);
  };
  var mouseLeave = function(d, i) {
    select(".bandsPERC" + i).classed("hoved", false);
    select(".stickPERC" + i).classed("hoved", false);
    select(".stick2PERC" + i).classed("hoved", false);
    select(".candlePERC" + i).classed("hoved", false);
    select(".dot" + customName).style("opacity", 0);

    select(".hHoverLinePERC")
      .style("opacity", 0)
      .attr("display", null);

    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip2.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    _this.displayOnHover(data[data.length - 1], _this);
  };

  TooltipB.style("opacity", 1)
    .style("left", svg.width + svg.margin.left + 1 + "px")
    .style(
      "top",
      svg.y(data[data.length - 1].close) + svg.margin.top - 9 + "px",
    )
    .html(format(_this._currencyFormat)(data[data.length - 1].close));

  // add vertical lines to show on hover
  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  _this.displayOnHover(data[data.length - 1], _this);
};

//hover method - on hover show lines and highlight candle
D3HoverPercentiles.prototype.header = function(_this) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#percentileDate").text(timeFormat(_this._dateFormat)(d.date));
      select("#percentileOpen").text(format(_this._currencyFormat)(d.open));
      select("#percentileHigh").text(format(_this._currencyFormat)(d.high));
      select("#percentileLow").text(format(_this._currencyFormat)(d.low));
      select("#percentileClose").text(format(_this._currencyFormat)(d.close));
      select("#percentileHover").text(format(_this._formatPercent)(d.perc));
    });
  }
  return headerRender;
};

D3HoverPercentiles.prototype.displayOnHover = function(d, _this) {
  select("#percentileControls")
    .datum(d)
    .call(this.header(_this));
};

// ==== Custom functions for Trends Chart =======================================================================
export const D3HoverTrends = function() {};

D3HoverTrends.prototype.hoverFct = function(
  svg,
  data,
  dataUp,
  dataDn,
  customName,
  _dateFormat,
  _currencyFormat,
  _formatPercent,
  _colorPositive,
  _colorNegative,
  svg2,
  colorTheme,
) {
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  this._formatPercent = _formatPercent;
  this._colorPositive = _colorPositive;
  this._colorNegative = _colorNegative;
  var _this = this;
  let tooltipWidth2 = 80;
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .style("color", textactive2(colorTheme))
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");
  let TooltipB = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("color", textactive2(colorTheme))
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .style("color", textactive2(colorTheme))
    .style(
      "background",
      customName === "TREND"
        ? lightbackground2(colorTheme)
        : colorDarkExpo(colorTheme),
    )
    .style(
      "border",
      customName === "TREND"
        ? lightbackground2(colorTheme)
        : colorDarkExpo(colorTheme),
    )
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    select(".hHoverLine" + customName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    let idx = dataUp.indexOf(d.date);
    if (idx > -1) {
      select(".stickUp" + idx).classed("hoved", true);
      select(".stick2Up" + idx).classed("hoved", true);
      select(".candleUp" + idx).classed("hoved", true);
    }
    idx = dataDn.indexOf(d.date);
    if (idx > -1) {
      select(".stickDn" + idx).classed("hoved", true);
      select(".stick2Dn" + idx).classed("hoved", true);
      select(".candleDn" + idx).classed("hoved", true);
    }

    let yAux = Math.max(-d.down_trend_change, d.up_trend_change);
    if (isFinite(yAux)) {
      yAux = (yAux === -d.down_trend_change ? -1 : 1) * yAux;
    } else {
      yAux = Number.isFinite(d.down_trend_change)
        ? Number.isFinite(d.up_trend_change)
          ? Math.max(-d.down_trend_change, d.up_trend_change)
          : d.down_trend_change
        : d.up_trend_change;
    }

    var bandWidth = svg.x.bandwidth();
    select(".dotCHANGE")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg2.x(d.date) + bandWidth / 2},${svg2.y(yAux)})`,
      );

    select(".bandsTREND" + i).classed("hoved", true);
    select(".bandsCHANGE" + i).classed("hoved", true);
    _this.displayOnHover(d, _this);

    Tooltip2.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_dateFormat)(d.date));

    let frmt =
      customName === "TREND"
        ? format(_this._currencyFormat)
        : format(_this._formatPercent);
    Tooltip3.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .html(frmt(svg.y.invert(yPixels)));
  };
  var mouseLeave = function(d, i) {
    select(".hHoverLine" + customName)
      .style("opacity", 0)
      .attr("display", null);

    let idx = dataUp.indexOf(d.date);
    if (idx > -1) {
      select(".stickUp" + idx).classed("hoved", false);
      select(".stick2Up" + idx).classed("hoved", false);
      select(".candleUp" + idx).classed("hoved", false);
    }
    idx = dataDn.indexOf(d.date);
    if (idx > -1) {
      idx = dataDn.indexOf(d.date);
      select(".stickDn" + idx).classed("hoved", false);
      select(".stick2Dn" + idx).classed("hoved", false);
      select(".candleDn" + idx).classed("hoved", false);
    }
    select(".dotCHANGE")
      .style("opacity", 0)
      .attr("display", null);
    select(".bandsTREND" + i).classed("hoved", false);
    select(".bandsCHANGE" + i).classed("hoved", false);
    _this.displayOnHover(data[data.length - 1], _this);

    Tooltip2.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  };

  TooltipB.style("opacity", 1)
    .style("left", svg.width + svg.margin.left + 1 + "px")
    .style(
      "top",
      svg.y(data[data.length - 1].close) + svg.margin.top - 9 + "px",
    )
    .html(format(_this._currencyFormat)(data[data.length - 1].close));
  // add vertical lines to show on hover
  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  dotCreate(
    svg,
    customName,
    2.5,
    textactive(colorTheme),
    grayLightBackground(colorTheme),
  );
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  _this.displayOnHover(data[data.length - 1], _this);
};

//hover method - on hover show lines and highlight candle
D3HoverTrends.prototype.header = function(_this) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#trendDate").text(timeFormat(_this._dateFormat)(d.date));
      select("#trendClose").text(format(_this._currencyFormat)(d.close));
      select("#trendChange")
        .text(
          format(_this._formatPercent)(
            Number.isNaN(d.up_trend_change) || d.up_trend_change === 0
              ? d.down_trend_change
              : d.up_trend_change,
          ),
        )
        .style(
          "color",
          Number.isNaN(d.up_trend_change) || d.up_trend_change === 0
            ? _this._colorNegative
            : _this._colorPositive,
        );
    });
  }
  return headerRender;
};

D3HoverTrends.prototype.displayOnHover = function(d, _this) {
  select("#trendControls")
    .datum(d)
    .call(this.header(_this));
};

// ==== Custom functions for Patterns Charts ==================================================================
export const D3HoverPatterns = function() {};

D3HoverPatterns.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _this,
  _dateFormat,
) {
  this._dateFormat = _dateFormat;
  let sharp = "#";
  let tooltipField = "tTip" + `${sharp}${svg.parentElement}`;
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let tooltipWidth2 = 80; // 0.1 * svg.width;
  svg[tooltipField] = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  var mouseOver = function(d, i) {
    if (d.highlight > 0) {
      _this.selectedPattern = d.highlight;
      _this.createSelectedPatternData();
      _this.createSelectedPatternChart(
        _this.D3ChartSelected,
        _this.chartDataSelected,
        false,
      );
    }
    select(this).classed("hoved", true);
    select(".stickPATTHIST" + i).classed("hoved", true);
    select(".stick2PATTHIST" + i).classed("hoved", true);
    select(".candlePATTHIST" + i).classed("hoved", true);

    svg[tooltipField]
      .style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(timeFormat(_dateFormat)(d.date));
  };
  var mouseLeave = function(d, i) {
    select(this).classed("hoved", false);
    select(".stickPATTHIST" + i).classed("hoved", false);
    select(".stick2PATTHIST" + i).classed("hoved", false);
    select(".candlePATTHIST" + i).classed("hoved", false);

    svg[tooltipField]
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  };

  // add vertical lines to show on hover
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  // _this.displayOnHover(data[data.length - 1], _this);
};

D3HoverPatterns.prototype.hoverFctPatterns = function(
  svg,
  data,
  customName,
  _dateFormat,
) {
  let sharp = "#";
  let tooltipField = "tTip" + `${sharp}${svg.parentElement}`;
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let tooltipWidth2 = 80;
  svg[tooltipField] = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip " + `${sharp}${svg.parentElement}`)
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  var mouseOver = function(d, i) {
    svg[tooltipField]
      .style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_dateFormat)(d.date));

    if (customName !== "PATT2b") {
      select(".bandsPATT1" + i).classed("hoved", true);
      select(".stickPATT1" + i).classed("hoved", true);
      select(".stick2PATT1" + i).classed("hoved", true);
      select(".candlePATT1" + i).classed("hoved", true);
      select(".bandsPATT2a" + i).classed("hoved", true);
      select(".stickPATT2a" + i).classed("hoved", true);
      select(".stick2PATT2a" + i).classed("hoved", true);
      select(".candlePATT2a" + i).classed("hoved", true);
    } else {
      select(".bandsPATT2b" + i).classed("hoved", true);
      select(".stickPATT2b" + i).classed("hoved", true);
      select(".stick2PATT2b" + i).classed("hoved", true);
      select(".candlePATT2b" + i).classed("hoved", true);
    }

    // _this.displayOnHover(d, _this);
  };
  var mouseLeave = function(d, i) {
    svg[tooltipField]
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    if (customName !== "PATT2b") {
      select(".bandsPATT1" + i).classed("hoved", false);
      select(".stickPATT1" + i).classed("hoved", false);
      select(".stick2PATT1" + i).classed("hoved", false);
      select(".candlePATT1" + i).classed("hoved", false);
      select(".bandsPATT2a" + i).classed("hoved", false);
      select(".stickPATT2a" + i).classed("hoved", false);
      select(".stick2PATT2a" + i).classed("hoved", false);
      select(".candlePATT2a" + i).classed("hoved", false);
    } else {
      select(".bandsPATT2b" + i).classed("hoved", false);
      select(".stickPATT2b" + i).classed("hoved", false);
      select(".stick2PATT2b" + i).classed("hoved", false);
      select(".candlePATT2b" + i).classed("hoved", false);
    }
    // _this.displayOnHover(data[data.length - 1], _this);
  };

  // add vertical lines to show on hover
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  // _this.displayOnHover(data[data.length - 1], _this);
};

D3HoverPatterns.prototype.hoverFctPatternBars = function(
  svg,
  data,
  barWidth = 0.7,
  name,
  color = "white",
  fillColor = "white",
  xField = "date",
  yField = "value",
  show = true,
  multipleColor = false,
  toZero = true,
  widthAux = 2,
  formatPercent,
) {
  let screenSize = document.getElementById("whole-page-div");
  let tooltipWidth =
    ((screenSize.clientWidth * 0.27 - svg.margin.left - svg.margin.right) * 3) /
    4;
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .attr("class", "tooltip")
    .style("font-size", "0.85rem")
    .style("width", tooltipWidth + "px")
    .html("");

  // var _this = this;
  var bandWidth = svg.x.bandwidth(); // from scale
  var candlewidth =
    Math.floor(min([bandWidth * barWidth, 1000000]) / 2) * 2 + 1; // give space between bars
  var delta = Math.round((bandWidth - candlewidth) / 2); // space given

  var mouseOverFct = function(d, i) {
    let col, txt, txt2, txt3;
    if (multipleColor === true) {
      col = color[i];
    } else {
      col = color;
    }
    if (d.id === 0) {
      txt2 = "Strong Bearish";
    } else if (d.id === 1) {
      txt2 = "Bearish";
    } else if (d.id === 2) {
      txt2 = "Weak Bearish";
    } else if (d.id === 3) {
      txt2 = "Weak Bullish";
    } else if (d.id === 4) {
      txt2 = "Bullish";
    } else if (d.id === 5) {
      txt2 = "Strong Bullish";
    }
    if (d.pattern >= d.all) {
      txt3 = "higher";
    } else {
      txt3 = "lower";
    }
    txt =
      "<strong>" +
      d.date.toUpperCase() +
      " </strong><span style='color:var(--textinactive)'>CASES:" +
      "<br>Recognized patterns had <strong>" +
      txt3 +
      "</strong> than average " +
      txt2 +
      " returns.</span><br><strong> " +
      formatPercent(d.pattern) +
      "</strong>" +
      "<span style='color:var(--textinactive)'> vs <span>" +
      formatPercent(d.all) +
      "</span></span>";

    select(".barPATT" + i).classed("hoved", true);
    select(".barREF" + i).classed("hoved", true);

    Tooltip.style("opacity", 1)
      .style("color", col)
      .style(
        "left",
        d.id * (1 + bandWidth - (1 / 5) * (tooltipWidth - bandWidth)) +
          svg.margin.left +
          "px",
      )
      .style("top", 0)
      .html(txt);

    // _this.displayOnHover(d, _this);
  };
  var mouseLeaveFct = function(d, i) {
    select(".barPATT" + i).classed("hoved", false);
    select(".barREF" + i).classed("hoved", false);
    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    // _this.displayOnHover(data[data.length - 1], _this);
  };
  // add bars and function on hover
  var balbar = svg
    .selectAll(".bars" + name)
    .data([data])
    .enter()
    .append("g")
    .attr("class", "bars" + name)
    .style("opacity", show);
  balbar
    .selectAll("rect")
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .style("fill", function(d, i) {
      return multipleColor ? fillColor[i] : fillColor;
    })
    .style("stroke", function(d, i) {
      return multipleColor ? color[i] : color;
    })
    .style("stroke-width", 1.5)
    .attr("x", function(d) {
      return svg.x(d[xField]) + delta;
    })
    .attr("y", function(d) {
      return Math.min(svg.y(0), svg.y(d[yField]));
    })
    .attr("class", function(d, i) {
      return "bar" + name + "" + i;
    })
    .attr("height", function(d) {
      return toZero === true ? Math.abs(svg.y(0) - svg.y(d[yField])) : widthAux;
    })
    .attr("width", candlewidth)
    .on("mouseover", mouseOverFct)
    .on("mouseleave", mouseLeaveFct);
  // _this.displayOnHover(data[data.length - 1], _this);
};

// ==== Custom functions for Valuation Chart ==================================================================
export const D3HoverValuation = function() {};

D3HoverValuation.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _dateFormat,
  _currencyFormat,
  _formatPercent,
  _term,
  dotColor,
  linesColor,
  svg1,
  svg2,
  svg3,
  colorTheme,
) {
  let axisFormatNumber =
    customName === "UPPER" || customName === "LL"
      ? _currencyFormat
      : _formatPercent;
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  this._formatPercent = _formatPercent;
  this._term = _term;
  this.svg1 = svg1;
  this.svg2 = svg2;
  this.svg3 = svg3;
  var _this = this;

  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let tooltipWidth2 = 80; // 0.1 * svg.width;
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    select(".hHoverLine" + customName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);
    if (customName === "UPPER") {
      select(".hHoverLineFWD")
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);
    }
    select(".bandsUPPER" + i).classed("hoved", true);
    select(".stickUPPER" + i).classed("hoved", true);
    select(".stick2UPPER" + i).classed("hoved", true);
    select(".candleUPPER" + i).classed("hoved", true);
    select(".bandsLL" + i).classed("hoved", true);
    select(".bandsLM" + i).classed("hoved", true);
    select(".bandsLR" + i).classed("hoved", true);
    if ("x" in _this.svg1) {
      const bandWidth = _this.svg1.x.bandwidth(); // from scale
      if (_this._term !== "ewma") {
        select(".dotLL")
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${_this.svg1.x(d.date) + bandWidth / 2},${_this.svg1.y(
              d["fwd-" + _this._term],
            )})`,
          );

        select(".dotLR")
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${_this.svg3.x(d.date) + bandWidth / 2},${_this.svg3.y(
              d["rr-" + _this._term],
            )})`,
          );
      }
      select(".dotLM")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${_this.svg2.x(d.date) + bandWidth / 2},${_this.svg2.y(
            d["vol-" + _this._term],
          )})`,
        );
    }
    _this.displayOnHover(d, _this);

    Tooltip2.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_dateFormat)(d.date));

    Tooltip3.style("opacity", 1)
      .style("right", svg.width + svg.margin.right + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .html(format(axisFormatNumber)(svg.y.invert(yPixels)));
  };
  var mouseLeave = function(d, i) {
    select(".bandsUPPER" + i).classed("hoved", false);
    select(".stickUPPER" + i).classed("hoved", false);
    select(".stick2UPPER" + i).classed("hoved", false);
    select(".candleUPPER" + i).classed("hoved", false);
    select(".bandsLL" + i).classed("hoved", false);
    select(".bandsLM" + i).classed("hoved", false);
    select(".bandsLR" + i).classed("hoved", false);
    select(".dotLL")
      .style("opacity", 0)
      .attr("display", null);
    select(".dotLM")
      .style("opacity", 0)
      .attr("display", null);
    select(".dotLR")
      .style("opacity", 0)
      .attr("display", null);
    _this.displayOnHover(data[data.length - 1], _this);

    select(".hHoverLine" + customName)
      .style("opacity", 0)
      .attr("display", null);
    if (customName === "UPPER") {
      select(".hHoverLineFWD")
        .style("opacity", 0)
        .attr("display", null);
    }

    Tooltip2.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  };

  // add vertical and horizontal lines to show on hover
  if (customName !== "UPPER") {
    dotCreate(svg, customName, 3, dotColor, linesColor);
  }
  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  _this.displayOnHover(data[data.length - 1], _this);
};

//hover method - on hover show lines and highlight candle
D3HoverValuation.prototype.header = function(_this) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#valuationDate").text(timeFormat(_this._dateFormat)(d.date));
      select("#valuationOpen").text(format(_this._currencyFormat)(d.open));
      select("#valuationHigh").text(format(_this._currencyFormat)(d.high));
      select("#valuationLow").text(format(_this._currencyFormat)(d.low));
      select("#valuationClose").text(format(_this._currencyFormat)(d.close));
      select("#valuationFwdH").text(
        format(_this._currencyFormat)(d["fwd-" + _this._term]),
      );
      select("#valuationVolH").text(
        format(_this._formatPercent)(d["vol-" + _this._term]),
      );
      select("#valuationVolEWMA").text(
        format(_this._formatPercent)(d["vol-ewma"]),
      );
      select("#valuationRRH").text(
        format(_this._formatPercent)(d["rr-" + _this._term]),
      );
    });
  }
  return headerRender;
};

D3HoverValuation.prototype.displayOnHover = function(d, _this) {
  select("#valuationControls")
    .datum(d)
    .call(this.header(_this));
};

// ==== Custom functions for FWD and VOLS Valuation =======================================================
export const D3HoverValuationFwd = function() {};

D3HoverValuationFwd.prototype.hoverFct = function(
  svg,
  data,
  spot,
  customName,
  _dateFormat,
  _currencyFormat,
  _formatPercent,
  dotColor,
  linesColor,
  showVol,
  lastDate,
  colorTheme,
) {
  let axisFormatNumber = _currencyFormat;
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  this._formatPercent = _formatPercent;
  this.showVol = showVol;
  var _this = this;
  var bandWidth = svg.x.bandwidth();
  let tooltipWidth = 300;
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".bands" + customName)
    .remove();
  let tooltipWidth2 = 80; // 0.1 * svg.width;
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");
  let Tooltip4 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("background", grayLightBackground(colorTheme))
    .style("border", grayLightBackground(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .attr("class", "tooltip")
    .style("background", darkBackground4(colorTheme))
    .style("font-size", "0.85rem")
    .style("z-index", 100000)
    .style("width", tooltipWidth + "px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    const strike = svg.y.invert(yPixels);
    let premiums = 0,
      volatility = 0,
      delta = 0,
      deltaType;
    if (_this.showVol === true) {
      volatility = qVolatility(d, strike);
      premiums = europeanBlackScholesFX(
        spot,
        strike,
        d.days / 365,
        d.r,
        d.rf,
        volatility,
      );
      if (d.value > strike) {
        deltaType = "PUT";
        delta = strikeToDelta(d, strike, spot, volatility, -1);
      } else {
        deltaType = "CALL";
        delta = strikeToDelta(d, strike, spot, volatility, 1);
      }
    }

    let txt;
    let t = (d.date - lastDate) / 86400000 / 365; // in years
    if (_this.showVol === true) {
      txt =
        "<table class='text-center table table-borderless table-sm table-tooltip mt-0 mb-0'>\n" +
        "  <thead>\n" +
        "    <tr>\n" +
        "      <th colspan='6' class='table-tooltip-bdr-b' style='min-width:270px; padding-bottom: 5px!important;" +
        "  font-weight: normal!important;'>" +
        "       PRICING DATA</th>\n" +
        "    </tr>\n" +
        "  </thead>\n" +
        "  <tbody>\n" +
        "    <tr>\n" +
        "      <td colspan='2' class='expo rem1'>&#8596;<span class='table-tooltip-small'> DATE</span><br/>\n" +
        timeFormat(_this._dateFormat)(d.date) +
        "</td>\n" +
        "      <td colspan='2'><span class='table-tooltip-small'>SPOT</span><br/>\n" +
        format(_this._currencyFormat)(spot) +
        "</td>\n" +
        "      <td colspan='2'><span class='table-tooltip-small'>FORWARD</span><br/>\n" +
        format(_this._currencyFormat)(d.value) +
        "</td>\n" +
        "    </tr>\n" +
        "    <tr class='table-tooltip-bdr-b'>\n" +
        "      <td colspan='3' class='text-center'><span class='table-tooltip-small'>SWAP POINTS</span><br/>\n" +
        format(_this._currencyFormat)(d.value - spot) +
        "</td>\n" +
        "      <td colspan='3' class='text-center'><span class='table-tooltip-small'>DEVALUATION</span><br/>\n" +
        format(_this._formatPercent)((d.value / spot) ** (1 / t) - 1) +
        "</td>\n" +
        "    </tr>\n" +
        "    <tr>\n" +
        "      <td colspan='2' class='expo rem1'>&#8597;<span class='table-tooltip-small'> STRIKE</span><br/>\n" +
        format(_this._currencyFormat)(strike) +
        "</td>\n" +
        "      <td colspan='2'><span class='table-tooltip-small'>VOLATILITY</span><br/>\n" +
        format(_this._formatPercent)(volatility) +
        "</td>\n" +
        "      <td colspan='2'><span class='table-tooltip-small'>&Delta; (" +
        deltaType +
        ")</span><br/>\n" +
        format(_this._formatPercent)(delta) +
        "</td>\n" +
        "    </tr>\n" +
        "    <tr>\n" +
        "      <td colspan='3' class='text-center rem11'><span class='table-tooltip-small' style='color: var(--call);'>CALL</span><br/>\n" +
        format(_this._currencyFormat)(premiums[0]) +
        "<sup class='ml-1'>(" +
        format(_this._formatPercent)(premiums[0] / spot) +
        ")</sup>\n" +
        "</td>\n" +
        "      <td colspan='3' class='text-center rem11'><span class='table-tooltip-small' style='color: var(--put);'>PUT</span><br/>\n" +
        format(_this._currencyFormat)(premiums[1]) +
        "<sup class='ml-1'>(" +
        format(_this._formatPercent)(premiums[1] / spot) +
        ")</sup>\n" +
        "</td>\n" +
        "    </tr>\n" +
        "  </tbody>\n" +
        "</table>\n";
      select(".dotFWD")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${svg.x(d.date) + bandWidth / 2},${yPixels})`,
        );
      Tooltip3.style("opacity", 1)
        .style("left", svg.width + svg.margin.left + 1 + "px")
        .style("top", yPixels + svg.margin.top - 9 + "px")
        .html(format(axisFormatNumber)(svg.y.invert(yPixels)));
      select(".hHoverLineFWD")
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);
      select(".hHoverLineUPPER")
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);
    } else {
      txt =
        "<table class='text-center table table-dark table-borderless table-sm table-tooltip'>\n" +
        "  <thead>\n" +
        "    <tr>\n" +
        "      <th colspan='6' class='table-tooltip-bdr-b' style='min-width: 200px; padding-bottom: 5px!important;'>" +
        "       PRICING DATA</th>\n" +
        "    </tr>\n" +
        "  </thead>\n" +
        "  <tbody>\n" +
        "    <tr>\n" +
        "      <td colspan='2' class='expo rem1'>&#8596;<span class='table-tooltip-small'> DATE</span><br/>\n" +
        timeFormat(_this._dateFormat)(d.date) +
        "</td>\n" +
        "      <td colspan='2'><span class='table-tooltip-small'>SPOT</span><br/>\n" +
        format(_this._currencyFormat)(spot) +
        "</td>\n" +
        "      <td colspan='2' class='expo rem1'><span class='table-tooltip-small'>FORWARD</span><br/>\n" +
        format(_this._currencyFormat)(d.value) +
        "</td>\n" +
        "    </tr>\n" +
        "    <tr>\n" +
        "      <td colspan='3' class='text-center'><span class='table-tooltip-small'>SWAP POINTS</span><br/>\n" +
        format(_this._currencyFormat)(d.value - spot) +
        "</td>\n" +
        "      <td colspan='3' class='text-center'><span class='table-tooltip-small'>DEVALUATION</span><br/>\n" +
        format(_this._formatPercent)((d.value / spot) ** (1 / t) - 1) +
        "</td>\n" +
        "    </tr>\n" +
        "  </tbody>\n" +
        "</table>\n";
      select(".dotFWD")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${svg.x(d.date) + bandWidth / 2},${svg.y(d.value)})`,
        );
      Tooltip3.style("opacity", 1)
        .style("left", svg.width + svg.margin.left + 1 + "px")
        .style("top", svg.y(d.value) + svg.margin.top - 9 + "px")
        .html(format(axisFormatNumber)(d.value));
      Tooltip4.style("opacity", 1)
        .style("left", svg.width + svg.margin.left + 1 + "px")
        .style("top", yPixels + svg.margin.top - 9 + "px")
        .html(format(axisFormatNumber)(svg.y.invert(yPixels)));

      select(".hHoverLineFWD")
        .style("opacity", 1)
        .attr("y1", svg.y(d.value))
        .attr("y2", svg.y(d.value));
      select(".hHoverLineUPPER")
        .style("opacity", 1)
        .attr("y1", svg.y(d.value))
        .attr("y2", svg.y(d.value));
    }

    select(".bandsFWD" + i).classed("hoved", true);

    let offset = -bandWidth;
    if (svg.x(d.date) > svg.width / 2) {
      offset = tooltipWidth;
    }
    let posTop, posBottom;
    if (_this.showVol === true) {
      if (strike > d.value) {
        posTop = yPixels + 10 + "px";
        posBottom = null;
      } else {
        posTop = null;
        posBottom =
          svg.height + svg.margin.top + svg.margin.bottom - yPixels + "px";
      }
    } else {
      if (strike > d.value) {
        posTop = svg.y(d.value) + 10 + "px";
        posBottom = null;
      } else {
        posTop = null;
        posBottom =
          svg.height +
          svg.margin.top +
          svg.margin.bottom -
          svg.y(d.value) +
          "px";
      }
    }
    select(".hHoverLine" + customName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    select(".hHoverLineUPPER")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    Tooltip.style("opacity", 1)
      .style("color", textactive(colorTheme))
      .style("left", svg.x(d.date) - offset + "px")
      .style("top", posTop)
      .style("bottom", posBottom)
      .html(txt);

    Tooltip2.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_dateFormat)(d.date));

    _this.displayOnHover(d, _this);
  };
  var mouseLeave = function(d, i) {
    select(".dotFWD")
      .style("opacity", 0)
      .attr("display", null);

    select(".hHoverLineFWD")
      .style("opacity", 0)
      .attr("display", null);
    select(".hHoverLineUPPER")
      .style("opacity", 0)
      .attr("display", null);

    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".bandsFWD" + i).classed("hoved", false);
    _this.displayOnHover(data[data.length - 1], _this);

    Tooltip2.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip4.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  };

  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  dotCreate(svg, customName, 2.5, dotColor, linesColor);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  _this.displayOnHover(data[data.length - 1], _this);
};

//hover method - on hover show lines and highlight candle
D3HoverValuationFwd.prototype.header = function(_this) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#valuationDate2").text(timeFormat(_this._dateFormat)(d.date));
      select("#valuationFwd").text(format(_this._currencyFormat)(d.value));
      select("#valuationATMFVol").text(format(_this._formatPercent)(d.atmfvol));
      select("#valuationEWMAVol").text(format(_this._formatPercent)(d.atmfvol));
      select("#valuationRR").text(format(_this._formatPercent)(d.rr));
    });
  }
  return headerRender;
};

D3HoverValuationFwd.prototype.displayOnHover = function(d, _this) {
  select("#valuationControls")
    .datum(d)
    .call(this.header(_this));
};

// ==== Custom functions for Relative Value Chart ==================================================================
export const D3HoverRV = function() {};

D3HoverRV.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _dateFormat,
  _currencyFormat,
  dotColor,
  linesColor,
  dot2Color,
  lines2Color,
  svg1,
  svg2,
  dotName,
  colorTheme,
  _context = {},
) {
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  this.svg1 = svg1;
  this.svg2 = svg2;
  var bandWidth = svg.x.bandwidth(); // from scale
  var _this = this;
  dotCreate(
    svg,
    `${customName}3`,
    2,
    textactive2(colorTheme),
    colorExpo(colorTheme),
    1.5,
  );

  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(`.tooltip${customName}`)
    .remove();

  let Tooltip2,
    Tooltip2b,
    Tooltip2Au,
    Tooltip2bAu,
    tooltipWidth2,
    Tooltip3,
    Tooltip4;
  if (customName === "RVC") {
    Tooltip2 = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", `tooltip${customName} tooltip`)
      .style("font-size", "0.8rem")
      .style("width", "60px")
      .html("");
    Tooltip2b = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", `tooltip${customName} tooltip`)
      .style("font-size", "0.8rem")
      .style("width", "60px")
      .html("");

    Tooltip2Au = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("color", textactive2(colorTheme))
      .style("border", dotColor)
      .style("background", dotColor)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", `tooltip${customName} tooltip`)
      .style("font-size", "0.8rem")
      .style("width", "60px")
      .html("");
    Tooltip2bAu = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("color", textactive2(colorTheme))
      .style("border", dot2Color)
      .style("background", dot2Color)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", `tooltip${customName} tooltip`)
      .style("font-size", "0.8rem")
      .style("width", "60px")
      .html("");
  } else {
    Tooltip4 = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", `tooltip${customName} tooltip`)
      .style("font-size", "0.8rem")
      .style("width", "60px")
      .html("");
  }
  tooltipWidth2 = 70;
  Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", `tooltip${customName} tooltip`)
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    select(".hHoverLine" + customName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    select(".bandsRV" + i).classed("hoved", true);
    if (_context.isCandles) {
      select(".stickRV" + i).classed("hoved", true);
      select(".stick2RV" + i).classed("hoved", true);
      select(".candleRV" + i).classed("hoved", true);
    } else {
      select(`.dot${customName}3`)
        .style("opacity", 1)
        .attr("transform", `translate(${svg.x(d.date)},${svg.y(d.close)})`);
    }

    select(".bandsRVC" + i).classed("hoved", true);
    select(".dotRVC")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${_this.svg2.x(d.date) + bandWidth / 2},
        ${_this.svg2.y(d.close)})`,
      );
    select(".dotRVC2")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${_this.svg2.x(d.date) + bandWidth / 2},
        ${_this.svg2.y2(d[dotName])})`,
      );
    if (customName === "RVC") {
      Tooltip2Au.style("opacity", 1)
        .style("right", svg.width + svg.margin.right + 1 + "px")
        .style("top", svg.y(d.close) + svg.margin.top - 9 + "px")
        .html(format(_this._currencyFormat)(d.close));
      Tooltip2bAu.style("opacity", 1)
        .style("left", svg.width + svg.margin.left + 1 + "px")
        .style("top", svg.y2(d[dotName]) + svg.margin.top - 9 + "px")
        .html(format(_this._currencyFormat)(d[dotName]));
      Tooltip2b.style("opacity", 1)
        .style("left", svg.width + svg.margin.left + 1 + "px")
        .style("top", yPixels + svg.margin.top - 9 + "px")
        .html(format(_this._currencyFormat)(svg.y2.invert(yPixels)));
      Tooltip2.style("opacity", 1)
        .style("right", svg.width + svg.margin.right + 1 + "px")
        .style("top", yPixels + svg.margin.top - 11 + "px")
        .html(format(_this._currencyFormat)(svg.y.invert(yPixels)));
    } else {
      Tooltip4.style("opacity", 1)
        .style("left", svg.width + svg.margin.left + 1 + "px")
        .style("top", yPixels + svg.margin.top - 9 + "px")
        .html(format(_this._currencyFormat)(svg.y.invert(yPixels)));
    }
    Tooltip3.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_dateFormat)(d.date));

    _this.displayOnHover(d, _this, dotName);
  };
  var mouseLeave = function(d, i) {
    select(".hHoverLine" + customName)
      .style("opacity", 0)
      .attr("display", null);

    select(".bandsRV" + i).classed("hoved", false);
    select(".stickRV" + i).classed("hoved", false);
    select(".stick2RV" + i).classed("hoved", false);
    select(".candleRV" + i).classed("hoved", false);
    select(".dot" + `${customName}3`).style("opacity", 0);
    select(".bandsRVC" + i).classed("hoved", false);
    select(".dotRVC")
      .style("opacity", 0)
      .attr("display", null);
    select(".dotRVC2")
      .style("opacity", 0)
      .attr("display", null);

    if (customName === "RVC") {
      Tooltip2b.style("left", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
      Tooltip2Au.style("right", 0)
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
      Tooltip2bAu.style("left", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
      Tooltip2.style("right", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
    } else {
      Tooltip4.style("left", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
    }
    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    _this.displayOnHover(data[data.length - 1], _this, dotName);
  };

  // add vertical lines to show on hover
  if (customName !== "RV") {
    dotCreate(svg, customName, 3, dotColor, linesColor);
    dotCreate(svg, customName + "2", 3, dot2Color, lines2Color);
  }
  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  _this.displayOnHover(data[data.length - 1], _this, dotName);
};

//hover method - on hover show lines and highlight candle
D3HoverRV.prototype.header = function(_this, dotName) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#relValDate").text(timeFormat(_this._dateFormat)(d.date));
      select("#relValClose").text(format(_this._currencyFormat)(d.close));
      select("#relValRef").text(format(_this._currencyFormat)(d[dotName]));
    });
  }
  return headerRender;
};

D3HoverRV.prototype.displayOnHover = function(d, _this, dotName) {
  select("#relValControls")
    .datum(d)
    .call(this.header(_this, dotName));
};

// ==== Custom functions for Relative Value Forecast Chart ==================================================================
export const D3HoverRVForecast = function() {};

D3HoverRVForecast.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _dateFormat,
  _currencyFormat,
  dotColor,
  linesColor,
  dot2Color,
  lines2Color,
  colorTheme,
) {
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  var bandWidth = svg.x.bandwidth(); // from scale
  let _this = this;

  let tooltipWidth = 180;
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(`.tooltip${customName}`)
    .remove();
  let yTooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", `tooltip${customName} tooltip`)
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  let tooltipWidth2 = 80;
  let xTooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", `tooltip${customName} tooltip`)
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  let TooltipB = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("background", dot2Color)
    .style("border", dot2Color)
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", `tooltip${customName} tooltip`)
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  TooltipB.style("opacity", 1)
    .style("left", svg.width + svg.margin.left + 1 + "px")
    .style("top", svg.y(data[0].path) + svg.margin.top - 9 + "px")
    .html(format(_this._currencyFormat)(data[0].path));
  let TooltipB2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("background", dotColor)
    .style("border", dotColor)
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", `tooltip${customName} tooltip`)
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");
  TooltipB2.style("opacity", 1)
    .style("left", svg.width + svg.margin.left + 1 + "px")
    .style("top", svg.y(data[0].rv) + svg.margin.top - 9 + "px")
    .html(format(_this._currencyFormat)(data[0].rv));

  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .attr("class", `tooltip${customName} tooltip`)
    .style("font-size", "1rem")
    .style("width", tooltipWidth + "px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );

    yTooltip
      .style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .html(format(_this._currencyFormat)(svg.y.invert(yPixels)));
    xTooltip
      .style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_this._dateFormat)(d.date));

    select(".hHoverLine" + customName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);
    select(".bandsRVF" + i).classed("hoved", true);
    select(".dotRVF")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg.x(d.date) + bandWidth / 2},
        ${svg.y(d.rv)})`,
      );
    select(".dotRVF2")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg.x(d.date) + bandWidth / 2},
        ${svg.y(d.path)})`,
      );

    TooltipB.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", svg.y(d.path) + svg.margin.top - 9 + "px")
      .html(format(_this._currencyFormat)(d.path));

    let posTop, posBottom;
    if (d.rv < d.path) {
      posTop = svg.y(d.rv) + 15 + "px";
      posBottom = null;
    } else {
      posTop = null;
      posBottom =
        svg.height +
        svg.margin.top +
        svg.margin.bottom -
        svg.y(d.rv) +
        10 +
        "px";
    }

    let txt =
      "<table class='text-center table table-dark table-borderless table-sm table-tooltip mt-0 mb-0'>\n" +
      "  <thead>\n" +
      "    <tr>\n" +
      "      <th colspan='2' class='table-tooltip-bdr-b' style='min-width: 180px; padding-bottom: 5px!important;'>" +
      "       Relative Value</th>\n" +
      "    </tr>\n" +
      "  </thead>\n" +
      "  <tbody>\n" +
      "    <tr>\n" +
      "      <td></td>\n" +
      "      <td></td>\n" +
      "    </tr>\n" +
      "    <tr>\n" +
      "      <td class='text-left'><span class='table-tooltip-small'>Date:</span></td>\n" +
      "      <td class='text-right'>" +
      timeFormat(_this._dateFormat)(d.date) +
      "</td>\n" +
      "    </tr>\n" +
      "    <tr>\n" +
      "      <td class='text-left'><span class='table-tooltip-small'>Objective:</span></td>\n" +
      "      <td class='text-right' style='color: var(--fwd)!important;'>" +
      format(_this._currencyFormat)(d.rv) +
      "</td>\n" +
      "    </tr>\n" +
      "    <tr>\n" +
      "      <td class='text-left'><span class='table-tooltip-small'>Path:</span></td>\n" +
      "      <td class='text-right' style='color: var(--symmetry)!important;'>" +
      format(_this._currencyFormat)(d.path) +
      "</td>\n" +
      "    </tr>\n" +
      "    </tr>\n" +
      "    <tr>\n" +
      "      <td class='text-left'><span class='table-tooltip-small'>Difference:</span></td>\n" +
      "      <td class='text-right'>" +
      format(_this._currencyFormat)(d.rv - d.path) +
      "</td>\n" +
      "    </tr>\n" +
      "  </tbody>\n" +
      "</table>\n";

    Tooltip.style("opacity", 1)
      .style("left", svg.x(d.date) - tooltipWidth + 6 + "px")
      .style("top", posTop)
      .style("bottom", posBottom)
      .html(txt);
  };
  var mouseLeave = function(d, i) {
    select(".hHoverLine" + customName)
      .style("opacity", 0)
      .attr("display", null);
    select(".bandsRVF" + i).classed("hoved", false);
    select(".dotRVF")
      .style("opacity", 0)
      .attr("display", null);
    select(".dotRVF2")
      .style("opacity", 0)
      .attr("display", null);
    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    TooltipB.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", svg.y(data[0].path) + svg.margin.top - 9 + "px")
      .html(format(_this._currencyFormat)(data[0].path));
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  };

  // add vertical lines to show on hover
  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  dotCreate(svg, customName, 3, dotColor, linesColor);
  dotCreate(svg, customName + "2", 3, dot2Color, lines2Color);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);
};

// ==== Custom functions for Projections Chart =======================================================
export const D3HoverForecast = function() {};

D3HoverForecast.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _dateFormat,
  _currencyFormat,
  colorTheme,
) {
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  var _this = this;
  let tooltipWidth2 = 80;
  const svgName = "PJ";
  let yTooltip = createTooltips(
    svg,
    this._currencyFormat,
    null,
    tooltipWidth2,
    this.colorTheme,
    svgName,
  );
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(svg, "PJ", grayLightBackground(colorTheme), 1);

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    select(".hHoverLineTA")
      .style("opacity", 1)
      .attr("x1", yPixels - svg.margin.top)
      .attr("x2", yPixels - svg.margin.top);
    select(`.bands${customName}${i}`).classed("hoved", true);
    select(`.stick${customName}${i}`).classed("hoved", true);
    select(`.stick2${customName}${i}`).classed("hoved", true);
    select(`.candle${customName}${i}`).classed("hoved", true);
    _this.displayOnHover(d, _this);

    const yt = svg.y;
    const xt = svg.x;
    let yRate = yt.invert(yPixels);
    let xRate = xt(d.date);
    yTooltip
      .style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .style("z-index", 10000002)
      .style("position", "absolute")
      .html(format(_this._currencyFormat)(yRate));

    xTooltip
      .style("opacity", 1)
      .style("left", xRate + svg.margin.left - tooltipWidth2 / 2 + "px")
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .style("position", "absolute")
      .html(timeFormat(_this._dateFormat)(d.date));
    select(".hHoverLinePJ")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);
  };

  var mouseLeave = function(d, i) {
    select(`.bands${customName}${i}`).classed("hoved", false);
    select(`.stick${customName}${i}`).classed("hoved", false);
    select(`.stick2${customName}${i}`).classed("hoved", false);
    select(`.candle${customName}${i}`).classed("hoved", false);

    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".hHoverLinePJ").style("opacity", 0);

    _this.displayOnHover(data[data.length - 1], _this);
  };

  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  yTooltip
    .style("left", "-1000000px")
    .style("top", "0px")
    .style("opacity", 0)
    .html("");
  xTooltip
    .style("left", "-1000000px")
    .style("top", "0px")
    .style("opacity", 0)
    .html("");
  select(".hHoverLinePJ").style("opacity", 0);
  _this.displayOnHover(data[data.length - 1], _this);
};

//hover method - on hover show lines and highlight candle
D3HoverForecast.prototype.header = function(_this) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#projectionDate").text(timeFormat("%b %d, %y")(d.date));
      select("#projectionClose").text(format(_this._currencyFormat)(d.close));
    });
  }
  return headerRender;
};

D3HoverForecast.prototype.displayOnHover = function(d, _this) {
  select("#projectionControls")
    .datum(d)
    .call(this.header(_this));
};

// ==== Custom functions for Projections Forecast Chart ==================================================================
export const D3HoverForecastFuture = function() {};

D3HoverForecastFuture.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _dateFormat,
  _currencyFormat,
  dotColor,
  linesColor,
  dot2Color,
  lines2Color,
  isCompare,
  entities = [],
) {
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  var bandWidth = svg.x.bandwidth(); // from scale
  let _this = this;

  let tooltipWidth = 220;
  let svgName = "PJF";
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(`.tooltip${svgName}`)
    .remove();
  select(`${sharp}${svg.parentElement}`)
    .selectAll(`.tooltip-info`)
    .remove();
  const XTooltipWidth = 80;
  let yTooltip = createTooltips(
    svg,
    this._currencyFormat,
    data[data.length - 1].mean,
    80,
    this.colorTheme,
    svgName,
  );
  yTooltip["lastYTooltip"];
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(svg, svgName, grayLightBackground(this.colorTheme), 1);
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .attr("class", "tooltip tooltip-info")
    .style("font-size", "1rem")
    .style("width", tooltipWidth + "px")
    .html("");

  var mouseOver = function(d, i) {
    select(".bandsPJF" + i).classed("hoved", true);
    if (isCompare) {
      select(".dotPJF")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${svg.x(d.date) + bandWidth / 2},
          ${svg.y(d.mean)})`,
        );
      select(".dotPJF2")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${svg.x(d.date) + bandWidth / 2},
          ${svg.y(d.median)})`,
        );

      let posTop = null;
      let posBottom =
        svg.height + svg.margin.top + svg.margin.bottom - svg.y(d.mean) + "px";
      let txt;
      if (d.update_date == null) {
        txt = `<table class='text-center table table-dark table-borderless table-sm table-tooltip mt-0 mb-0'>
  <thead>
    <tr>
      <th colspan='2' class='table-tooltip-bdr-b' style='min-width:180px; padding-bottom: 5px!important;'>       PROJECTIONS</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td></td>
      <td></td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Date:</span></td>
      <td class='text-right'>${timeFormat("%b %d, %y")(d.date)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Median:</span></td>
      <td class='text-right' style='color: var(--hedge)!important;'>${format(
        _this._currencyFormat,
      )(d.median)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Mean:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.mean)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Mean - Stdev:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.mean - d.standard_dev)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Mean + Stdev:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.mean + d.standard_dev)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Stdev:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.standard_dev)}</td>
    </tr>
  </tbody>
</table>
`;
      } else {
        txt = `<table class='text-center table table-dark table-borderless table-sm table-tooltip mt-0 mb-0'>
  <thead>
    <tr>
      <th colspan='2' class='table-tooltip-bdr-b' style='min-width:180px; padding-bottom: 5px!important;'>       PROJECTIONS</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td></td>
      <td></td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Date:</span></td>
      <td class='text-right'>${timeFormat("%b %d, %y")(d.date)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Median:</span></td>
      <td class='text-right' style='color: var(--hedge)!important;'>${format(
        _this._currencyFormat,
      )(d.median)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Mean:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.mean)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Mean - Stdev:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.mean - d.standard_dev)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Mean + Stdev:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.mean + d.standard_dev)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Stdev:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.standard_dev)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Min:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.min)}</td>
    </tr>
    <tr>
      <td class='text-left'><span class='table-tooltip-small'>Max:</span></td>
      <td class='text-right' style='color: var(--symmetry)!important;'>${format(
        _this._currencyFormat,
      )(d.max)}</td>
    </tr>
  </tbody>
</table>
`;
      }
      event.preventDefault();
      const xPixels = event.layerX;
      Tooltip.style("opacity", 1)
        .style("left", xPixels - tooltipWidth + "px")
        .style("top", posTop)
        .style("bottom", posBottom)
        .html(txt);
    } else {
      for (let i = 0; i < entities.length; i++) {
        select(".dotPJF" + entities[i])
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${svg.x(d.date) + bandWidth / 2},
            ${svg.y(d[entities[i]])})`,
          );
      }
    }
    const yt = svg.y;
    const xt = svg.x;
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    let yRate = yt.invert(yPixels);
    let xRate = xt(d.date);
    yTooltip
      .style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .style("position", "absolute")
      .html(format(_this._currencyFormat)(yRate));
    xTooltip
      .style("opacity", 1)
      .style("left", xRate + svg.margin.left - XTooltipWidth / 2 + "px")
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .style("position", "absolute")
      .html(timeFormat(_this._dateFormat)(d.date));
    select(".hHoverLinePJF")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);
    _this.displayOnHover(d, _this, isCompare, entities);
  };
  var mouseLeave = function(d, i) {
    select(".bandsPJF" + i).classed("hoved", false);
    yTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".hHoverLinePJF").style("opacity", 0);

    if (isCompare) {
      select(".dotPJF")
        .style("opacity", 0)
        .attr("display", null);
      select(".dotPJF2")
        .style("opacity", 0)
        .attr("display", null);
      Tooltip.style("left", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
      _this.displayOnHover(data[data.length - 1], _this, isCompare, entities);
    } else {
      for (let i = 0; i < entities.length; i++) {
        select(".dotPJF" + entities[i])
          .style("opacity", 0)
          .attr("display", null);
      }
    }
  };

  // add vertical lines to show on hover
  if (isCompare) {
    dotCreate(svg, customName, 3, dotColor, linesColor);
    dotCreate(svg, customName + "2", 3, dot2Color, lines2Color);
  } else {
    for (let i = 0; i < entities.length; i++) {
      dotCreate(svg, customName + entities[i], 3, dotColor[i], linesColor);
    }
  }
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  _this.displayOnHover(data[data.length - 1], _this, isCompare, entities);
};

//hover method - on hover show lines and highlight candle
D3HoverForecastFuture.prototype.header = function(_this, isCompare, entities) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#projectionDate2").text(timeFormat("%b %d, %y")(d.date));
      if (isCompare) {
        select("#projectionMean").text(format(_this._currencyFormat)(d.mean));
        select("#projectionStdev").text(
          format(_this._currencyFormat)(d.mean - d.standard_dev) +
            " | " +
            format(_this._currencyFormat)(d.mean + d.standard_dev),
        );
        select("#projectionMedian").text(
          format(_this._currencyFormat)(d.median),
        );
      } else {
        let sharp = "#",
          mean = "Mean";
        for (let i = 0; i < entities.length; i++) {
          select(`${sharp}${entities[i]}${mean}`).text(
            format(_this._currencyFormat)(d[entities[i]]),
          );
        }
      }
    });
  }
  return headerRender;
};

D3HoverForecastFuture.prototype.displayOnHover = function(
  d,
  _this,
  isCompare,
  entities,
) {
  select("#projectionControls")
    .datum(d)
    .call(this.header(_this, isCompare, entities));
};

// ======= Curve Analysis ================================= //

export const D3HoverCurveAnalysisFuture = function() {};

D3HoverCurveAnalysisFuture.prototype.hoverFct = function(
  svg,
  data,
  customName,
  _dateFormat,
  _currencyFormat,
  dotColor,
  linesColor,
  dot2Color,
  lines2Color,
  isCompare,
  entities = [],
) {
  this._dateFormat = _dateFormat;
  this._currencyFormat = _currencyFormat;
  var bandWidth = svg.x.bandwidth(); // from scale
  let _this = this;
  const viewCurveAnalysisFuture = function(d, i) {
    select(".rectCAF")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg.x(d.date) + bandWidth / 2},
      ${svg.y(d.min)})`,
      );
    select(".rectCAF2")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg.x(d.date) + bandWidth / 2},
          ${svg.y(d.max)})`,
      );
    select(".rectCAF3")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg.x(d.date) + bandWidth / 2},
          ${svg.y(d.current)})`,
      );
    select(".rectCAF4")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg.x(d.date) + bandWidth / 2},
          ${svg.y(data[i + 1].current)})`,
      );
  };

  rectsCreate(svg, customName, 12, 6, dotColor, linesColor);
  rectsCreate(svg, customName + "2", 12, 6, dot2Color, lines2Color);
  rectsCreate(svg, customName + "3", 12, 6, dot2Color, lines2Color);
  rectsCreate(svg, customName + "4", 12, 6, "yellow", lines2Color);
  bandsCreateCA(data, svg, customName, viewCurveAnalysisFuture);

  _this.displayOnHover(data[data.length - 1], _this, isCompare, entities);
};

//hover method - on hover show lines and highlight candle
D3HoverCurveAnalysisFuture.prototype.header = function(
  _this,
  isCompare,
  entities,
) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#projectionDate2").text(timeFormat(_this._dateFormat)(d.date));
      if (isCompare) {
        select("#projectionMean").text(format(_this._currencyFormat)(d.min));
        select("#projectionStdev").text(
          format(_this._currencyFormat)(d.min - d.standard_dev) +
            " | " +
            format(_this._currencyFormat)(d.min + d.standard_dev),
        );
        select("#projectionMedian").text(format(_this._currencyFormat)(d.max));
      } else {
        let sharp = "#",
          min = "Mean";
        for (let i = 0; i < entities.length; i++) {
          select(`${sharp}${entities[i]}${min}`).text(
            format(_this._currencyFormat)(d[entities[i]]),
          );
        }
      }
    });
  }
  return headerRender;
};

D3HoverCurveAnalysisFuture.prototype.displayOnHover = function(
  d,
  _this,
  isCompare,
  entities,
) {
  select("#projectionControls")
    .datum(d)
    .call(this.header(_this, isCompare, entities));
};

// ==== Custom functions for Projections Forecast Chart ==================================================================
export const D3HoverCommodities = function() {};

D3HoverCommodities.prototype.hoverFct = function(
  svg,
  dataName,
  customName,
  dateFormat,
  currencyFormat,
  formatPercent,
  dotColor = "white",
  linesColor = "white",
  processData,
  lineWidth,
  fontColor,
  color = "color",
  colorTheme,
  _context,
  hoveredLineWidth = null,
) {
  hoveredLineWidth = hoveredLineWidth == null ? lineWidth : hoveredLineWidth;
  this.dateFormat = dateFormat;
  this.currencyFormat = currencyFormat;
  this.formatPercent = formatPercent;
  let bandWidth = svg.x.bandwidth(); // from scale
  let _this = this;
  let data = _context[dataName];
  let tooltipDateWidth = 80;
  let sharp = "#";

  let hoverF = function(svg, data, i, ym) {
    let arr = [];
    data.series.forEach((d) => arr.push(Math.abs(d.values[i] - ym)));
    return processData.indexOfMin(arr);
  };

  let tooltipWidth = 280;
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  svg.Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .attr("class", "tooltip")
    .style("font-size", "1rem")
    .style("width", tooltipWidth + "px")
    .html("");

  let tooltipDate = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .style("color", textactive2(colorTheme))
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .attr("class", "tooltip tooltipDate")
    .style("font-size", "0.8rem")
    .style("width", tooltipDateWidth + "px")
    .html("");

  let tooltipSpot = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .style("color", textactive2(colorTheme))
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .attr("class", "tooltip tooltipSpot")
    .style("font-size", "0.8rem")
    .style("width", "60px")
    .html("");

  const mouseOver = function(d, i) {
    data = _context[dataName];
    select(".bandsCOMM" + i).classed("hoved", true);
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );

    let s = hoverF(svg, data, i, svg.y.invert(yPixels - svg.margin.top));
    let txt = `
    <table class='text-center table table-dark table-borderless table-sm table-tooltip mt-0 mb-0'>
      <thead>
        <tr>
          <th colspan='3' class='table-tooltip-bdr-b' style='min-width:280px; padding-bottom: 5px!important; font-weight: normal!important;'>
            ${data.series[s].short_name} ${data.series[s].currRepresentation}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><span class='table-tooltip-small'>DATE:</span></td>
          <td><span class='table-tooltip-small'>PRICE:</span></td>
          <td><span class='table-tooltip-small'>CHANGE:</span></td>
        </tr>
        <tr>
          <td class='text-center'>
            ${timeFormat(_this.dateFormat)(data.dates[0])}
          </td>
          <td class='text-center'>
            ${format(_this.currencyFormat)(data.series[s].valuesP[0])}
          </td>
          <td class='text-center'></td>
        </tr>
        <tr>
          <td>
            ${timeFormat(_this.dateFormat)(data.dates[i])}
          </td>
          <td>
            ${format(_this.currencyFormat)(data.series[s].valuesP[i])}
          </td>
          <td>
            ${_this.formatPercent(data.series[s].valuesPerc[i])}
          </td>
        </tr>
        <tr>
          <td>
            ${timeFormat(_this.dateFormat)(data.dates[data.dates.length - 1])}
          </td>
          <td>
            ${format(_this.currencyFormat)(
              data.series[s].valuesP[data.series[s].valuesP.length - 1],
            )}
          </td>
          <td>
            ${_this.formatPercent(data.series[s].latestP)}
          </td>
        </tr>
      </tbody>
    </table>
    `;

    select(".dotCOMM")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svg.x(d) + bandWidth / 2},
          ${svg.y(data.series[s].values[i])})`,
      );
    const hovedName = cleanAndUnionName(data.series[s].short_name);

    function applyStyleAndClass(element, name) {
      function isHovedName(d) {
        return cleanAndUnionName(d.short_name) === name;
      }
      element
        .style("stroke-width", (d) =>
          isHovedName(d) ? hoveredLineWidth : lineWidth,
        )
        .classed("highlightedBind", (d) => isHovedName(d));
    }

    function handleSortLines(svgElement, hovedName) {
      svgElement
        .style("stroke-width", function(d) {
          const shortName = cleanAndUnionName(d.name);
          return this.style.strokeWidth === "0"
            ? "0"
            : shortName === hovedName
            ? hoveredLineWidth
            : lineWidth;
        })
        .classed(
          "highlightedBind",
          (d) => cleanAndUnionName(d.name) === hovedName,
        );
    }

    applyStyleAndClass(svg.path, hovedName);
    handleSortLines(svg.sortlines1, hovedName);
    handleSortLines(svg.sortlines2, hovedName);
    handleSortLines(svg.sortlines3, hovedName);

    svg.legendsB.attr("stroke", function(d) {
      const shortName = cleanAndUnionName(d.name);
      return shortName === hovedName ? textactive(colorTheme) : null;
    });
    svg.legendsT.attr("fill", function(d) {
      const shortName = cleanAndUnionName(d.name);
      return shortName === hovedName ? textactive(colorTheme) : fontColor;
    });

    select(".hHoverLineCOMM")
      .style("opacity", 1)
      .attr("y1", yPixels + "px")
      .attr("y2", yPixels + "px");

    svg.Tooltip.style("opacity", 1)
      .style("color", textactive(colorTheme))
      .style("left", svg.margin.left + 2 + "px")
      .style("top", svg.margin.top + 2 + "px")
      .html(txt);

    tooltipDate
      .style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipDateWidth / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(_this.dateFormat)(d.date));

    tooltipSpot
      .style("opacity", 1)
      .style("right", svg.width + svg.margin.right + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .html(format(_this.currencyFormat)(svg.y.invert(yPixels)));
  };
  const mouseLeave = function(d, i) {
    select(".bandsCOMM" + i).classed("hoved", false);

    select(".hHoverLineCOMM")
      .style("opacity", 0)
      .attr("display", null);

    select(".dotCOMM")
      .style("opacity", 0)
      .attr("display", null);

    svg.path.style("stroke-width", lineWidth).classed("highlightedBind", false);
    svg.sortlines1
      .style("stroke-width", function() {
        if (this.style.strokeWidth === "0") {
          return "0";
        } else {
          return lineWidth;
        }
      })
      .classed("highlightedBind", false);
    svg.sortlines2
      .style("stroke-width", function() {
        if (this.style.strokeWidth === "0") {
          return "0";
        } else {
          return lineWidth;
        }
      })
      .classed("highlightedBind", false);

    svg.sortlines3
      .style("stroke-width", lineWidth)
      .classed("highlightedBind", false);
    svg.legendsB.attr("stroke", function(d) {
      return _context[color](isFinite(d.value) ? d.value : 0);
    });
    svg.legendsT.attr("fill", fontColor);
    svg.Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    tooltipDate
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    tooltipSpot
      .style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  };

  // add vertical lines to show on hover
  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  dotCreate(svg, customName, 3, dotColor, linesColor, 3);
  bandsCreate(data.dates, svg, customName, mouseOver, mouseLeave);
};

//hover method - on hover show lines and highlight candle
D3HoverCommodities.prototype.header = function(_this) {
  function headerRender(selection) {
    selection.each(function(d) {
      select("#projectionDate2").text(timeFormat(_this._dateFormat)(d.date));
      select("#projectionMean").text(format(_this._currencyFormat)(d.mean));
      select("#projectionStdev").text(
        format(_this._currencyFormat)(d.mean - d.standard_dev) +
          " | " +
          format(_this._currencyFormat)(d.mean + d.standard_dev),
      );
      select("#projectionMedian").text(format(_this._currencyFormat)(d.median));
    });
  }
  return headerRender;
};

D3HoverCommodities.prototype.displayOnHover = function(d, _this) {
  select("#projectionControls")
    .datum(d)
    .call(this.header(_this));
};

D3HoverCommodities.prototype.updateElementsChart = function({
  _this,
  data,
  color,
  lineWidth = 2,
  selectedVar = -1,
  nameField = "short_name",
  colorSelection,
  sortedData,
  idx = "idx",
  latestField = "latest",
  onMouseOut = false,
  onHoverElements = false,
  isEvent = false,
  isUpdateHoverElements = false,
  elementLegends = {
    opacity: 1,
    strokeWidth: 2,
    onClickLegends: () => {},
    direction: "right",
    name: "NAME",
    nameAuxField: "short_name",
    color: null,
  },
}) {
  const svg = _this.svgNode;
  const bandWidth = svg.x.bandwidth();
  const line = lineD3()
    .curve(curveLinear)
    .x((d, i) => svg.x(data.dates[i]) + bandWidth / 2)
    .y((d) => svg.y(d));
  // update multilines
  svg.path = svg.path.data(data.series).join(
    function(enter) {
      return enter
        .append("path")
        .style("stroke", (d, i) => colorSelection[i])
        .style("stroke-width", lineWidth)
        .attr("class", function(d, i) {
          let nameNonSpaces = cleanAndUnionName(d[nameField]);
          return `multiLines${nameNonSpaces ? nameNonSpaces : i}`;
        })
        .attr("d", (d) => line(d.values))
        .classed("highlighted", function(d, i) {
          return i === selectedVar;
        })
        .style("shape-rendering", "crispEdges")
        .style("vector-effect", "non-scaling-stroke");
    },
    function(update) {
      return update
        .style("stroke", (d, i) => colorSelection[i])
        .style("stroke-width", lineWidth)
        .attr("class", function(d, i) {
          let nameNonSpaces = cleanAndUnionName(d[nameField]);
          return `multiLines${nameNonSpaces ? nameNonSpaces : i}`;
        })
        .attr("d", (d) => line(d.values))
        .classed("highlighted", function(d, i) {
          return i === selectedVar;
        })
        .style("shape-rendering", "crispEdges")
        .style("vector-effect", "non-scaling-stroke");
    },
    function(exit) {
      return exit.remove();
    },
  );

  svg.sortlines1 = svg.sortlines1
    .data(function() {
      return sortedData.map(function(d, i) {
        return { name: d[nameField], value: d[latestField], idx: i };
      });
    })
    .join(
      function(enter) {
        return enter
          .append("line")
          .attr("x1", svg.xRight((1 / 8) * svg.xMax) - 5)
          .attr("x2", svg.xRight((1 / 8) * svg.xMax) + 1)
          .attr("y1", function(d) {
            return svg.y(d.value);
          })
          .attr("y2", function(d) {
            return svg.y(d.value);
          })
          .style("opacity", function(d) {
            return +isFinite(d.value);
          })
          .attr("class", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return "sortLineOne" + nameNonSpaces;
          })
          .style("stroke-width", lineWidth)
          .style("display", null)
          .attr("stroke", function(d) {
            return color(isFinite(d.value) ? d.value : 0);
          })
          .classed("highlightedBind", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return nameNonSpaces === selectedVar;
          })
          .style("shape-rendering", "crispEdges")
          .style("vector-effect", "non-scaling-stroke")
          .on("mouseover", function(d) {
            if (isEvent) {
              onHoverElements(d);
            }
          })
          .on("mouseout", function() {
            if (isEvent) {
              onMouseOut();
            }
          });
      },
      function(update) {
        return update
          .attr("y1", function(d) {
            return svg.y(d.value);
          })
          .attr("y2", function(d) {
            return svg.y(d.value);
          })
          .attr("class", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return "sortLineOne" + nameNonSpaces;
          })
          .attr("stroke", function(d) {
            return color(isFinite(d.value) ? d.value : 0);
          })
          .on("mouseover", function(d) {
            if (isEvent) {
              onHoverElements(d);
            }
          })
          .on("mouseout", function() {
            if (isEvent) {
              onMouseOut();
            }
          });
      },
      function(exit) {
        return exit.remove();
      },
    );

  svg.sortlines2 = svg.sortlines2
    .data(function() {
      return sortedData.map(function(d, i) {
        return { name: d[nameField], value: d[latestField], idx: i };
      });
    })
    .join(
      function(enter) {
        return enter
          .append("line")
          .attr("x1", svg.xRight((1 / 8) * svg.xMax))
          .attr("x2", svg.xRight((3 / 8) * svg.xMax))
          .attr("y1", function(d) {
            return svg.y(d.value);
          })
          .attr("y2", function(d) {
            return svg.height - svg.step * d.idx;
          })
          .style("opacity", function(d) {
            return +isFinite(d.value);
          })
          .attr("class", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return "sortLineTwo" + nameNonSpaces;
          })
          .style("stroke-width", lineWidth)
          .style("display", null)
          .attr("stroke", function(d) {
            return color(isFinite(d.value) ? d.value : 0);
          })
          .classed("highlightedBind", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return nameNonSpaces === selectedVar;
          })
          .style("shape-rendering", "crispEdges")
          .style("vector-effect", "non-scaling-stroke")
          .on("mouseover", function(d) {
            if (isEvent) {
              onHoverElements(d);
            }
          })
          .on("mouseout", function() {
            if (isEvent) {
              onMouseOut();
            }
          });
      },
      function(update) {
        return update
          .attr("y1", function(d) {
            return svg.y(d.value);
          })
          .attr("class", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return "sortLineTwo" + nameNonSpaces;
          })
          .classed("highlightedBind", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return nameNonSpaces === selectedVar;
          })
          .attr("stroke", function(d) {
            return color(isFinite(d.value) ? d.value : 0);
          })
          .on("mouseover", function(d) {
            if (isEvent) {
              onHoverElements(d);
            }
          })
          .on("mouseout", function() {
            if (isEvent) {
              onMouseOut();
            }
          });
      },
      function(exit) {
        return exit.remove();
      },
    );

  svg.sortlines3 = svg.sortlines3
    .data(function() {
      return sortedData.map(function(d, i) {
        return { name: d[nameField], value: d[latestField], idx: i };
      });
    })
    .join(
      function(enter) {
        return enter
          .append("line")
          .attr("x1", svg.xRight((3 / 8) * svg.xMax))
          .attr("x2", svg.xRight((1 / 2) * svg.xMax))
          .attr("y1", function(d) {
            return svg.height - svg.step * d.idx;
          })
          .attr("y2", function(d) {
            return svg.height - svg.step * d.idx;
          })
          .style("opacity", function(d) {
            return +isFinite(d.value);
          })
          .attr("class", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return "sortLineThree" + nameNonSpaces;
          })
          .style("stroke-width", lineWidth)
          .style("display", null)
          .attr("stroke", function(d) {
            return color(isFinite(d.value) ? d.value : 0);
          })
          .classed("highlightedBind", function(d) {
            let nameNonSpaces = d[idx];
            if (idx === "name") {
              nameNonSpaces = cleanAndUnionName(nameNonSpaces);
            }
            return nameNonSpaces === selectedVar;
          })
          .style("shape-rendering", "crispEdges")
          .style("vector-effect", "non-scaling-stroke")
          .on("mouseover", function(d) {
            if (isEvent) {
              onHoverElements(d);
            }
          })
          .on("mouseout", function() {
            if (isEvent) {
              onMouseOut();
            }
          });
      },
      function(update) {
        return update.attr("stroke", function(d) {
          return color(isFinite(d.value) ? d.value : 0);
        });
      },
      function(exit) {
        return exit.remove();
      },
    );

  const gap = 1;
  const xPosition = (dir = elementLegends.direction) =>
    dir === "right" ? (1 / 2) * svg.xMax : (5 / 8) * svg.xMax;

  const rectTransform = function() {
    if (elementLegends.direction === "right") {
      return svg.xRight(xPosition()) + 3 * gap;
    } else {
      return svg.xLeft(xPosition()) + 3 * gap;
    }
  };

  const rectWidth = function() {
    return svg.xRight((1 / 8) * svg.xMax) - svg.xRight(0) - gap * 2;
  };
  // Update legends
  svg.legendsB = svg.legendsB
    .data(function() {
      return sortedData.map(function(d, i) {
        return {
          name: d[nameField],
          aux_name: d[elementLegends.nameAuxField],
          value: d[latestField],
          idx: i,
        };
      });
    })
    .join(
      function(enter) {
        return enter
          .append("rect")
          .attr("x", rectTransform)
          .attr("y", function(d) {
            return (
              svg.height -
              svg.step * d.idx -
              (svg.xRight((1 / 8) * svg.xMax) - svg.xRight(0)) / 2 +
              gap
            );
          })
          .attr("class", function(d) {
            const nameNonSpaces = cleanAndUnionName(d.name);
            return "legendB" + elementLegends.name + "" + nameNonSpaces;
          })
          .attr("width", rectWidth)
          .attr("height", rectWidth)
          .attr("fill", function(d) {
            return elementLegends.color(isFinite(d.value) ? d.value : 0);
          })
          .attr("stroke", function(d) {
            return elementLegends.color(isFinite(d.value) ? d.value : 0);
          })
          .attr("stroke-width", elementLegends.strokeWidth)
          .classed("highlightedBind", function(d) {
            return d.idx === selectedVar;
          })
          .on("click", function(d) {
            if (isEvent) {
              elementLegends.onClickLegends(d, elementLegends.name);
            }
          })
          .on("mouseover", function(d) {
            if (isEvent) {
              onHoverElements(d, elementLegends.name);
            }
          })
          .on("mouseout", function() {
            if (isEvent) {
              onMouseOut();
            }
          });
      },
      function(update) {
        return update
          .attr("fill", function(d) {
            return elementLegends.color(isFinite(d.value) ? d.value : 0);
          })
          .attr("stroke", function(d) {
            return elementLegends.color(isFinite(d.value) ? d.value : 0);
          });
      },
      function(exit) {
        return exit.remove();
      },
    );

  // Update hover elements
  this.updateHoverElements(svg, _this, isUpdateHoverElements);
};

D3HoverCommodities.prototype.removeElements = function(_this) {
  _this.svgNode.selectAll(".bandsCOMM").remove();
  _this.svgNode.selectAll(".dotCOMM").remove();
  _this.svgNode.selectAll(".hHoverLineCOMM").remove();
};

D3HoverCommodities.prototype.updateHoverElements = function(
  svg,
  _this,
  isCall,
) {
  if (isCall) {
    //Remove elements with hover
    this.removeElements(_this);
    //Create elements with hover
    this.hoverFct(
      svg,
      "commoditiesDataFilterHover",
      "COMM",
      _this.dateFormat,
      _this.currencyFormat,
      _this.formatPercent1,
      "white",
      textactive(_this.colorTheme),
      _this.processData,
      2,
      textinactive(_this.colorTheme),
      "color",
      _this.colorTheme,
      _this,
      4,
    );
  }
};
// === markets comparison
export const D3HoverComparison = function() {};

D3HoverComparison.prototype.hoverFct = function(
  svg,
  data,
  formatNumber,
  dateFormat,
  customName,
  isTwoAxes,
  colorY1,
  colorY2,
  svgUp,
  svgMid,
  svgLow,
  fieldName1,
  fieldName2,
  fieldName3,
  chartNum = 0,
) {
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("color", "white")
    .style(
      "background",
      chartNum === 1 && isTwoAxes === false ? "black" : colorY1,
    )
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "68px")
    .html("");
  let Tooltip2b = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("color", "white")
    .style(
      "background",
      chartNum === 1 && isTwoAxes === false
        ? "black"
        : isTwoAxes && chartNum === 1
        ? colorY2
        : colorY1,
    )
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "68px")
    .html("");
  let tooltipWidth2 = 70;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );

    select(".hHoverLine" + customName)
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    select(".dotUP")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svgUp.x(d.date) + svg.x.bandwidth() / 2},${svgUp.y(
          d[fieldName1],
        )})`,
      );
    if (fieldName2 !== null) {
      select(".dotUPb")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${svgUp.x(d.date) + svgUp.x.bandwidth() / 2},${
            isTwoAxes ? svgUp.y2(d[fieldName2]) : svgUp.y(d[fieldName2])
          })`,
        );
    }
    select(".dotMID")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svgMid.x(d.date) + svgMid.x.bandwidth() / 2},${svgMid.y(
          d[fieldName3],
        )})`,
      );
    select(".dotLOW")
      .style("opacity", 1)
      .attr(
        "transform",
        `translate(${svgLow.x(d.date) + svgLow.x.bandwidth() / 2},${svgLow.y(
          d.corr,
        )})`,
      );

    select(".bandsUP" + i).classed("hoved", true);
    select(".bandsMID" + i).classed("hoved", true);
    select(".bandsLOW" + i).classed("hoved", true);

    Tooltip3.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 2 + "px")
      .html(timeFormat(dateFormat)(d.date));

    Tooltip2.style("opacity", 1)
      .style("right", svg.width + svg.margin.right + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .html(formatNumber(svg.y.invert(yPixels)));

    Tooltip2b.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", yPixels + svg.margin.top - 9 + "px")
      .html(
        formatNumber(
          isTwoAxes && chartNum === 1
            ? svg.y2.invert(yPixels)
            : svg.y.invert(yPixels),
        ),
      );
    populateTableComparison(
      {
        date: d.date,
        main: d[fieldName1],
        second: d[fieldName2],
        spread: d[fieldName3],
        corr: d.corr,
      },
      formatNumber,
      timeFormat("%d %b %y"),
    );
  };

  var mouseLeave = function(d, i) {
    select(".hHoverLine" + customName)
      .style("opacity", 0)
      .attr("display", null);

    select(".dotUP")
      .style("opacity", 0)
      .attr("display", null);
    if (fieldName2 !== null) {
      select(".dotUPb")
        .style("opacity", 0)
        .attr("display", null);
    }
    select(".dotMID")
      .style("opacity", 0)
      .attr("display", null);
    select(".dotLOW")
      .style("opacity", 0)
      .attr("display", null);

    Tooltip2.style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    Tooltip2b.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".bandsUP" + i).classed("hoved", false);
    select(".bandsMID" + i).classed("hoved", false);
    select(".bandsLOW" + i).classed("hoved", false);

    let dat = data[data.length - 1];
    populateTableComparison(
      {
        date: dat.date,
        main: dat[fieldName1],
        second: dat[fieldName2],
        spread: dat[fieldName3],
        corr: dat.corr,
      },
      formatNumber,
      timeFormat("%d %b %y"),
    );
  };

  hLineCreate(svg, customName, grayLightBackground(this.colorTheme), 1);
  dotCreate(svg, customName, 2, "white", colorY1, 1.5);
  if (chartNum === 1) {
    dotCreate(svg, customName + "b", 2, "white", colorY2, 1.5);
  }
  bandsCreate(data, svg, customName, mouseOver, mouseLeave, mouseOver);

  let d = data[data.length - 1];
  populateTableComparison(
    {
      date: d.date,
      main: d[fieldName1],
      second: d[fieldName2],
      spread: d[fieldName3],
      corr: d.corr,
    },
    formatNumber,
    timeFormat("%d %b %y"),
  );
};
